import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import {
  exportPurchaseLinesCsv,
  fetchPurchaseLines,
  type PurchaseLine,
  type PurchaseLinesFilter,
} from './api';

const { Text } = Typography;

export interface PurchaseLinesDrawerProps {
  /** What to list; null closes the drawer. */
  filter: PurchaseLinesFilter | null;
  title: string;
  onClose: () => void;
}

/** The AutoCount purchase lines behind one figure: a month from the chart, or a supplier from the bars. */
export function PurchaseLinesDrawer({ filter, title, onClose }: PurchaseLinesDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { data, error, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['analytics', 'purchases', 'lines', filter],
    queryFn: ({ pageParam }) => fetchPurchaseLines(filter as PurchaseLinesFilter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: filter !== null,
  });

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  const download = async (): Promise<void> => {
    if (filter === null) return;
    try {
      const csv = await exportPurchaseLinesCsv(filter);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase-lines-${String(filter.year)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('insights.purchasing.lines.downloadFailed'));
    }
  };

  const columns: TableProps<PurchaseLine>['columns'] = [
    {
      title: t('insights.lines.date'),
      key: 'date',
      // A Kuala Lumpur calendar date from AutoCount: shown as is, never shifted.
      render: (_: unknown, r) => <span className="tabular">{dayjs(r.docDate).format('D MMM YYYY')}</span>,
    },
    {
      title: t('insights.lines.document'),
      key: 'doc',
      render: (_: unknown, r) => (
        <Flex vertical>
          <Text className="tabular">{r.docNo}</Text>
          {r.supplierInvoiceNo !== null && <Text type="secondary">{r.supplierInvoiceNo}</Text>}
        </Flex>
      ),
    },
    {
      title: t('insights.purchasing.lines.supplier'),
      key: 'supplier',
      render: (_: unknown, r) => orDash(r.creditorName ?? r.creditorCode),
    },
    {
      title: t('insights.lines.item'),
      key: 'item',
      render: (_: unknown, r) => (
        <Flex vertical>
          <Text className="tabular">{orDash(r.itemCode)}</Text>
          {r.description !== null && <Text type="secondary">{r.description}</Text>}
        </Flex>
      ),
    },
    { title: t('insights.lines.qty'), key: 'qty', align: 'right', render: (_: unknown, r) => r.qty },
    {
      title: t('insights.purchasing.lines.amount'),
      key: 'amount',
      align: 'right',
      render: (_: unknown, r) => formatMoney(r.amount),
    },
  ];

  return (
    <Drawer
      open={filter !== null}
      title={title}
      onClose={onClose}
      width={960}
      className="nks-action-drawer"
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            void download();
          }}
        >
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {error !== null && (
          <Text type="danger">{error instanceof ApiError ? error.message : t('insights.lines.loadFailed')}</Text>
        )}
        <Table<PurchaseLine>
          rowKey="uid"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={filter !== null && isPending}
          locale={{ emptyText: t('insights.lines.empty') }}
          dataSource={rows}
          columns={columns}
        />
        {hasNextPage && (
          <Button
            block
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('insights.lines.loadMore')}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}
